<template>
  <BaseModal
    :title="$i18next.t('message.ai.dubbings.modal.description')"
    hookSufix="DubbingModal"
    :noDefaultPadding="noDefaultPadding"
    :onCloseHook="onCloseHook"
    :onOpenHook="onOpenHook"
  >
    <template #content>
      <v-container class="pa-0">
        <template v-if="currentModalStage.globalStage === 'initial'">
          <DefaultInitialState
            :subtitle="$i18next.t('message.ai.dubbings.modal.description')"
            :cardData="initialStageCardsData"
          />
        </template>
        <template v-if="currentModalStage.globalStage === 'generate'">
          <StepStage
            :subStage="currentModalStage.subStage"
            @changeSubStage="updateSubStage"
            :totalSteps="4"
          >
            <template #step_1>
              <p
                class="text-h7 mt-8 mb-11 text-center"
                :class="{ 'neutral--text': !$vuetify.theme.dark }"
              >
                {{
                  $i18next.t(
                    'message.ai.dubbings.modal.auto.step_one.description'
                  )
                }}
              </p>
              <div class="d-flex flex-column container__setup-action">
                <template v-if="!subtitles.length">
                  <v-radio-group
                    v-model="generateDubbingData.dubbingOption"
                    hide-details
                    @change="generateDubbingData.dubbingOption = 0"
                    class="mt-0 pt-0 radio__ai"
                  >
                    <v-radio :value="0">
                      <template #label>
                        <span
                          :class="{ 'neutral--text': !$vuetify.theme.dark }"
                        >
                          {{
                            $i18next.t(
                              'message.ai.dubbings.modal.auto.step_one.options.transcription.label'
                            )
                          }}
                        </span>
                      </template>
                    </v-radio>
                  </v-radio-group>
                  <div
                    :class="{
                      'not-allowed': generateDubbingData.dubbingOption !== 0,
                    }"
                  >
                    <v-select
                      v-model="generateDubbingData.dubbingFromVideo"
                      :disabled="generateDubbingData.dubbingOption !== 0"
                      class="v-input__custom-colorized select__dubbing-option ml-9 align-self-end mt-1"
                      outlined
                      hide-details
                      :placeholder="
                        $i18next.t(
                          'message.ai.dubbings.modal.auto.step_one.options.transcription.placeholder'
                        )
                      "
                      item-text="label"
                      item-value="srclang"
                      :value="generateDubbingData.dubbingFromVideo"
                      :menu-props="{ top: false, offsetY: true }"
                      :class="{
                        dark: $vuetify.theme.dark,
                      }"
                      :no-data-text="
                        $t('message.pages.videoEditModal.usedSubtitles')
                      "
                      :items="availableTranscriptionLangCodes"
                      dense
                    >
                      <template
                        #prepend-inner
                        v-if="
                          generateDubbingData.dubbingFromVideo &&
                          generateDubbingData.dubbingFromVideo !== 'auto'
                        "
                      >
                        <country-flag
                          :country="
                            getCountryCodeBySrcLang(
                              generateDubbingData.dubbingFromVideo
                            )
                          "
                        />
                      </template>
                      <template #append>
                        <v-icon> mdi-chevron-down </v-icon>
                      </template>
                    </v-select>
                  </div>
                </template>
                <div
                  :class="{ 'mt-6': !subtitles.length }"
                  v-if="subtitles.length"
                >
                  <v-radio-group
                    v-model="generateDubbingData.dubbingOption"
                    hide-details
                    @change="generateDubbingData.dubbingOption = 1"
                    class="mt-0 pt-0 radio__ai"
                  >
                    <v-radio :value="1">
                      <template #label>
                        <span
                          :class="{ 'neutral--text': !$vuetify.theme.dark }"
                        >
                          {{
                            $i18next.t(
                              'message.ai.dubbings.modal.auto.step_one.options.translation.label'
                            )
                          }}
                        </span>
                      </template>
                    </v-radio>
                  </v-radio-group>
                  <div
                    class="align-self-end select__dubbing-option"
                    :class="{
                      'not-allowed': generateDubbingData.dubbingOption !== 1,
                    }"
                  >
                    <v-select
                      :disabled="generateDubbingData.dubbingOption !== 1"
                      v-model="generateDubbingData.dubbingFromSubtitle"
                      class="v-input__custom-colorized ml-9 mt-1"
                      outlined
                      hide-details
                      :placeholder="
                        $i18next.t(
                          'message.ai.dubbings.modal.auto.step_one.options.translation.placeholder'
                        )
                      "
                      item-text="label"
                      item-value="srclang"
                      :value="generateDubbingData.dubbingFromSubtitle"
                      :menu-props="{ top: false, offsetY: true }"
                      :class="{
                        dark: $vuetify.theme.dark,
                      }"
                      :no-data-text="
                        $t('message.pages.videoEditModal.usedSubtitles')
                      "
                      :items="availableSubtitleLangsToDub"
                      dense
                    >
                      <template
                        #prepend-inner
                        v-if="generateDubbingData.dubbingFromSubtitle"
                      >
                        <country-flag
                          :country="
                            getCountryCodeBySrcLang(
                              generateDubbingData.dubbingFromSubtitle
                            )
                          "
                        />
                      </template>
                      <template #append>
                        <v-icon> mdi-chevron-down </v-icon>
                      </template>
                    </v-select>
                  </div>
                </div>
              </div>
            </template>
            <template #step_2>
              <p
                class="text-h7 mt-4 mb-0 text-center"
                :class="{ 'neutral--text': !$vuetify.theme.dark }"
              >
                {{
                  $i18next.t('message.ai.dubbings.modal.auto.step_two.title')
                }}
              </p>
              <div class="subtitle-1 mt-0 mb-6 pb-1 text-center"></div>
              <!-- <p
                class="subtitle-1 mt-1 mb-12 pb-1 text-center"
                :class="{
                  'neutral--text text--lighten-1': !$vuetify.theme.dark,
                }"
              >
                {{
                  $i18next.t('message.ai.dubbings.modal.auto.step_two.subtitle')
                }}
              </p> -->
              <div class="container__content-list mb-4">
                <v-row no-gutters class="mb-1">
                  <v-col class="d-flex align-center">
                    <v-simple-checkbox
                      :color="!checkboxIndeterminateStatus ? 'primary' : ''"
                      :indeterminate="checkboxIndeterminateStatus"
                      :ripple="false"
                      :value="isAnyAiDubbingItemSelected"
                      @input="selectAllItems"
                    ></v-simple-checkbox>
                    <span
                      class="subtitle-1"
                      :class="{
                        'neutral--text text--lighten-1': !$vuetify.theme.dark,
                      }"
                      >{{
                        $i18next.t(
                          'message.ai.dubbings.modal.auto.step_two.selectAll'
                        )
                      }}</span
                    >
                  </v-col>
                  <v-col>
                    <v-row no-gutters justify="end"
                      ><div class="container__search">
                        <v-text-field
                          v-model="
                            generateDubbingData.searchAvailableLangsFilter
                          "
                          dense
                          outlined
                          class="v-text-field__custom-colorized"
                          height="32px"
                          hide-details
                          :placeholder="
                            $i18next.t(
                              'message.ai.dubbings.modal.auto.step_two.search'
                            )
                          "
                          prepend-inner-icon="mdi-magnify"
                        >
                        </v-text-field></div
                    ></v-row>
                  </v-col>
                </v-row>

                <div class="container__upload-list--modal">
                  <div
                    class="d-flex"
                    v-for="(item, index) of filteredAiDubbingOptions"
                    :key="`filteredList_${item.country}_${index}`"
                  >
                    <v-simple-checkbox
                      color="primary"
                      :ripple="false"
                      :value="checkIfSelected(item)"
                      @input="checkboxSelection(item)"
                    ></v-simple-checkbox>
                    <div
                      class="d-flex py-3 align-center f-width list-item__bordered"
                    >
                      <country-flag
                        :country="item.country"
                        class="list-item__flag--filtered"
                      ></country-flag>
                      <span
                        class="subtitle-1 font-weight-medium mr-6 list-item__text"
                        :class="{
                          'neutral--text text--lighten-1': !$vuetify.theme.dark,
                        }"
                        >{{ item.label }}</span
                      >
                      <div class="d-flex justify-space-between f-width">
                        <template
                          v-if="generateDubbingData.dubbingOption === 0"
                        >
                          <template v-if="canShowRowValues(item)">
                            <!-- <span
                              class="text-subtitle-2 text-subtitle-2__standardized--modal font-weight-regular"
                              :class="{
                                'neutral--text text--lighten-1':
                                  !$vuetify.theme.dark,
                              }"
                            >
                              {{ getMultiplierAmount(item) }}
                              {{
                                $i18next.t(
                                  'message.ai.dubbings.modal.auto.step_two.credits'
                                )
                              }}/min
                            </span>
                            <span
                              class="text-subtitle-2 text-subtitle-2__standardized--modal font-weight-regular"
                              :class="{
                                'neutral--text text--lighten-1':
                                  !$vuetify.theme.dark,
                              }"
                              >{{ getItemCost(item) }}
                              {{
                                $i18next.t(
                                  'message.ai.dubbings.modal.auto.step_two.credits'
                                )
                              }}</span
                            > -->
                          </template>
                        </template>
                        <template v-else>
                          <!-- <span
                            class="text-subtitle-2 text-subtitle-2__standardized--modal font-weight-regular"
                            :class="{
                              'neutral--text text--lighten-1':
                                !$vuetify.theme.dark,
                            }"
                          >
                            {{ getMultiplierAmount(item) }}
                            {{
                              $i18next.t(
                                'message.ai.dubbings.modal.auto.step_two.credits'
                              )
                            }}/min</span
                          >
                          <span -->
                          <!-- class="text-subtitle-2 text-subtitle-2__standardized--modal font-weight-regular"
                            :class="{
                              'neutral--text text--lighten-1':
                                !$vuetify.theme.dark,
                            }"
                            >{{ getItemCost(item) }}
                            {{
                              $i18next.t(
                                'message.ai.dubbings.modal.auto.step_two.credits'
                              )
                            }}</span>
                          --></template
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <template #step_3>
              <p
                class="text-h7 my-8 text-center"
                :class="{ 'neutral--text': !$vuetify.theme.dark }"
              >
                {{
                  $i18next.t('message.ai.dubbings.modal.auto.step_three.title')
                }}
              </p>
              <div
                class="d-flex flex-column text-center container__content-list"
              >
                <v-row
                  class="mt-5"
                  no-gutters
                  v-if="currentDubVoiceListing.length > 1"
                >
                  <v-col>
                    <v-switch
                      v-model="generateDubbingData.sameVoiceToAll"
                      inset
                      class="v-switch__custom-colorized mt-0 pt-0 ml-1 mb-1"
                      @change="handleSetSameVoiceToAll"
                      hide-details
                    >
                      <template #label>
                        <span
                          class="subtitle-1"
                          :class="{ 'neutral--text': !$vuetify.theme.dark }"
                          >{{
                            $i18next.t(
                              'message.ai.dubbings.modal.auto.step_three.sameVoiceSwitchLabel'
                            )
                          }}</span
                        >
                      </template>
                    </v-switch>
                  </v-col>
                </v-row>
                <div class="container__upload-list--modal padded">
                  <div
                    class="d-flex"
                    v-for="(item, index) of currentDubVoiceListing"
                    :key="`filteredList_${item.country}_${index}`"
                  >
                    <div
                      class="d-flex py-3 align-center f-width list-item__bordered"
                    >
                      <country-flag
                        :country="item.country"
                        class="list-item__flag--filtered"
                      ></country-flag>
                      <span
                        class="subtitle-1 font-weight-medium mr-6 text-start list-item__text"
                        :class="{ 'neutral--text': !$vuetify.theme.dark }"
                        >{{ item.label }}</span
                      >
                      <div class="d-flex f-width justify-end">
                        <v-select
                          v-model="
                            generateDubbingData.dubbingSelectedVoices[index]
                          "
                          @change="
                            handleSetSameVoiceToAll(), handleStopCurrentAudio()
                          "
                          class="v-input__custom-colorized--small-dub"
                          outlined
                          :disabled="
                            Boolean(index) && generateDubbingData.sameVoiceToAll
                          "
                          hide-details
                          :placeholder="
                            $i18next.t(
                              'message.ai.dubbings.modal.auto.step_three.placeholder'
                            )
                          "
                          item-text="label"
                          item-value="value"
                          :menu-props="{ top: false, offsetY: true }"
                          :class="{
                            dark: $vuetify.theme.dark,
                          }"
                          :no-data-text="
                            $t('message.pages.videoEditModal.usedSubtitles')
                          "
                          :items="availableAiVoices"
                          dense
                        >
                          <template #append>
                            <span
                              class="ml-1 text-caption blueMarine--text align-self-center"
                            >
                              voice powered by
                              <b style="font-size: 10px">IIElevenLabs</b>
                            </span>
                            <v-icon> mdi-chevron-down </v-icon>
                          </template>
                        </v-select>

                        <template
                          v-if="
                            audioPlayState.isFetchingAudio.includes(
                              item.country
                            )
                          "
                        >
                          <div
                            class="d-flex align-center mr-1 ml-2"
                            style="position: relative"
                          >
                            <v-progress-circular
                              :size="16"
                              :width="3"
                              color="brand"
                              style="position: absolute"
                            ></v-progress-circular>
                            <v-progress-circular
                              :size="16"
                              :width="3"
                              color="brand"
                              indeterminate
                            ></v-progress-circular>
                          </div>
                        </template>
                        <template v-else>
                          <template
                            v-if="
                              generateDubbingData.dubbingSelectedVoices[
                                index
                              ] === 'original'
                            "
                          >
                            <v-icon size="28" color="transparent"
                              >mdi-play</v-icon
                            >
                          </template>
                          <template v-else>
                            <template
                              v-if="
                                audioPlayState.isPlaying.includes(item.country)
                              "
                            >
                              <v-icon
                                size="28"
                                color="primary"
                                @click="handleStopCurrentAudio(item)"
                                class="no-ripple"
                                >mdi-pause</v-icon
                              >
                            </template>
                            <template v-else>
                              <v-icon
                                size="28"
                                @click="handlePlayCurrentAudio(item, index)"
                                color="primary"
                                class="no-ripple"
                                >mdi-play</v-icon
                              >
                            </template>
                          </template>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <audio
                :src="audioPlayState.audioURL.blobURL"
                ref="audioPlay"
              ></audio>
            </template>
            <template #finalScreen>
              <div
                class="d-flex align-center text-center flex-column container__final-screen"
              >
                <img
                  :src="require(`@/assets/generatingAiSubtitle.svg`)"
                  class="mt-12 pt-12"
                />
                <span
                  class="text-h6 font-weight-medium mt-7 mb-2"
                  :class="{
                    'brand--text text--darken-1': !$vuetify.theme.dark,
                  }"
                  >{{
                    $i18next.t(
                      'message.ai.dubbings.modal.auto.final_screen.title'
                    )
                  }}</span
                >
                <span
                  class="subtitle-1"
                  :class="{ 'neutral--text': !$vuetify.theme.dark }"
                  >{{
                    $i18next.t(
                      'message.ai.dubbings.modal.auto.final_screen.subtitle'
                    )
                  }}</span
                >
                <BaseBtn class="mt-8" @click="handleClose">{{
                  $i18next.t(
                    'message.ai.dubbings.modal.auto.final_screen.actionBtn'
                  )
                }}</BaseBtn>
              </div>
            </template>
          </StepStage>
        </template>
        <template v-else-if="currentModalStage.globalStage === 'upload'">
          <UploadStage
            :loadings="loadings"
            :unusedLangCodes="unusedLangCodes"
            :subStage="currentModalStage.subStage"
            :accept="['.mp3']"
            :subtitle="
              $i18next.t('message.ai.dubbings.modal.upload.step_one.title')
            "
            type="dub"
            @changeSubStage="updateSubStage"
          />
        </template>
      </v-container>
    </template>
    <template #card-actions>
      <template>
        <v-row
          flat
          dense
          no-gutters
          class="ma-auto"
          :class="{
            'card-height-stp4': isStepFour,
          }"
        >
          <v-card
            flat
            tile
            width="800"
            :class="{
              'bg-color card-height': isStepTwo && !isUploadState,
              'card-height-stp4': isStepFour,
            }"
          >
            <v-row v-if="isStepTwo" align="center" justify="center">
              <v-col cols="auto">
                <v-card
                  flat
                  tile
                  height="50"
                  width="auto"
                  class="d-flex align-center"
                  :class="{
                    'bg-color': !isUploadState,
                  }"
                >
                  <!--  -->
                  <div
                    v-if="isAnyAiDubbingItemSelected && !isUploadState"
                    class="d-flex flex-column align-center align-items-center"
                  >
                    <v-row no-gutters>
                      <div class="text-center d-flex flex-column align-center">
                        <div
                          class="d-flex justify-center align-center flex-wrap"
                        >
                          <span
                            v-if="!isBatch"
                            class="mr-2"
                            :class="{
                              'text-neutral neutral--text':
                                !$vuetify.theme.dark,
                            }"
                            >{{
                              $t('message.ai.aiWorkFlowChargeModal.aiCost')
                            }}</span
                          >
                          <span
                            v-else
                            class="mr-2"
                            :class="{
                              'text-neutral neutral--text':
                                !$vuetify.theme.dark,
                            }"
                            >{{
                          $t('message.ai.aiWorkFlowChargeModal.batchAiCost',
                          {count: batchAllVideosCount, nounNumber: nounNumber}),
                            }}</span
                          >
                          <v-card
                            width="18"
                            height="18"
                            flat
                            class="mr-1 d-flex"
                          >
                            <v-img
                              src="@/assets/credit_small_icon.svg"
                              class="align-center bg-color-icon pt-1"
                            />
                          </v-card>
                          <span
                            class="text-body-1 line-height--fixed"
                            :class="{
                              'neutral--text': !$vuetify.theme.dark,
                            }"
                            >{{ generatedDubbingTotalPrice }}
                          </span>
                        </div>
                        <small>
                          ({{ $t('message.ai.aiWorkFlowChargeModal.promo') }})
                        </small>
                      </div>
                    </v-row>
                  </div>
                  <div
                    v-if="isAnyAiDubbingItemSelected || isUploadState"
                    class="d-flex flex-row align-center align-items-center"
                  ></div>
                  <div
                    v-else
                    class="d-flex flex-row align-center align-items-center"
                  >
                    <span
                      class="mr-2"
                      :class="{
                        'text-neutral neutral--text': !$vuetify.theme.dark,
                      }"
                      >{{ $t('message.ai.noSelectedOption') }}</span
                    >
                  </div>
                </v-card>
              </v-col>
            </v-row>
            <empty-state
              v-if="!hasEnoughCredits && isStepTwo && !isUploadState"
              :logoConfig="logoConfig"
              :arrowConfig="arrowConfig"
              :textConfig="textConfig"
            />
            <template v-else>
              <v-row dense no-gutters>
                <div>
                  <div
                    class="d-flex justify-center f-width py-2 pl-8"
                    v-if="errors.generate"
                  >
                    <span class="red--text text-center">{{
                      errors.generate
                    }}</span>
                  </div>
                  <v-card
                    flat
                    tile
                    width="800"
                    class="pa-0 v-card__actions--bot-position"
                    :class="{
                      'v-card__actions--color': isActionButtonsHighlighted,
                    }"
                    v-if="showCardButtons"
                  >
                    <template v-if="isActionButtonsHighlighted">
                      <v-col cols="12" class="credit-spend-col pa-0">
                        <v-card
                          flat
                          class="credit-spend-card bg-color bg-color-icon"
                          height="95px"
                        >
                          <v-spacer></v-spacer>
                          <CreditSpendCard
                            :amount="creditAmount"
                            :spendPrice="generatedDubbingTotalPrice"
                            :subCalc="creditDubRemainCalc"
                            parentModalKey="DubbingModal"
                            :class="{
                              'hide-card': !isAnyAiDubbingItemSelected,
                            }"
                          />
                        </v-card>
                        <div class="d-flex justify-center">
                          <BaseBtn
                            v-if="
                              !(
                                currentModalStage.globalStage === 'upload' &&
                                currentModalStage.subStage === 3
                              ) && !forwardBtnData.generationCompleted
                            "
                            type="secondary"
                            @click="handleGoBack"
                            :disabled="
                              isAnyLoadingRunning ||
                              forwardBtnData.isBatchGeneration
                            "
                            class="mx-2"
                            style="width: 100px"
                          >
                            {{ $i18next.t('message.pages.video.back') }}
                          </BaseBtn>
                          <template v-if="forwardBtnData.condition">
                            <v-tooltip v-if="forwardBtnData.tooltip" top>
                              <template v-slot:activator="{ on, attrs }">
                                <BaseBtn
                                  class="mx-2"
                                  :class="{ 'btn-width': !isStepThree }"
                                  v-bind="attrs"
                                  v-on="{ ...on, ...$listeners }"
                                  :disabled="forwardBtnData.isDisabled"
                                  :loading="forwardBtnData.isLoading"
                                  @click="forwardBtnData.onClick"
                                >
                                  {{ forwardBtnData.label }}
                                </BaseBtn>
                              </template>
                              <span>{{ forwardBtnData.tooltip }}</span>
                            </v-tooltip>

                            <BaseBtn
                              v-else
                              class="mx-2"
                              :class="{ 'btn-width': !isStepThree }"
                              :disabled="forwardBtnData.isDisabled"
                              :loading="forwardBtnData.isLoading"
                              @click="forwardBtnData.onClick"
                            >
                              {{ forwardBtnData.label }}
                            </BaseBtn>
                          </template>
                        </div>
                      </v-col>
                    </template>
                    <v-col cols="12" class="pa-0 mb-4">
                      <v-card
                        flat
                        :class="{
                          'v-card__actions--color': isActionButtonsHighlighted,
                        }"
                      >
                        <div
                          v-if="
                            !isStepTwo ||
                            currentModalStage.globalStage === 'upload'
                          "
                          class="d-flex justify-center"
                        >
                          <BaseBtn
                            v-if="
                              !(
                                currentModalStage.globalStage === 'upload' &&
                                currentModalStage.subStage === 3
                              ) && !forwardBtnData.generationCompleted
                            "
                            type="secondary"
                            @click="handleGoBack"
                            :disabled="
                              isAnyLoadingRunning ||
                              forwardBtnData.isBatchGeneration
                            "
                            class="mx-2"
                            style="width: 100px"
                          >
                            {{ $i18next.t('message.pages.video.back') }}
                          </BaseBtn>
                          <template v-if="forwardBtnData.condition">
                            <v-tooltip v-if="forwardBtnData.tooltip" top>
                              <template v-slot:activator="{ on, attrs }">
                                <BaseBtn
                                  class="mx-2"
                                  :class="{ 'btn-width': !isStepThree }"
                                  v-bind="attrs"
                                  v-on="{ ...on, ...$listeners }"
                                  :disabled="forwardBtnData.isDisabled"
                                  :loading="forwardBtnData.isLoading"
                                  @click="forwardBtnData.onClick"
                                >
                                  {{ forwardBtnData.label }}
                                </BaseBtn>
                              </template>
                              <span>{{ forwardBtnData.tooltip }}</span>
                            </v-tooltip>

                            <BaseBtn
                              v-else
                              class="mx-2"
                              :class="{ 'btn-width': !isStepThree }"
                              :disabled="forwardBtnData.isDisabled"
                              :loading="forwardBtnData.isLoading"
                              @click="forwardBtnData.onClick"
                            >
                              {{ forwardBtnData.label }}
                            </BaseBtn>
                          </template>
                        </div>
                      </v-card>
                    </v-col>
                  </v-card>
                </div>
              </v-row>
            </template>
          </v-card>
        </v-row>
      </template>
    </template>
  </BaseModal>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import Uppy from '@uppy/core'
import tus from '@uppy/tus'
import Storage from '@/utils/Storage'
import i18n from '@/translations/lang'
import UploadStage from './AddSubtitleModal/UploadStage.vue'
import store from '@/store'
import BaseBtn from '@/components/BaseBtn.vue'
import BaseModal from '@/components/BaseModal.vue'
import DefaultInitialState from './AddSubtitleModal/DefaultInitialStage.vue'
import StepStage from './AddSubtitleModal/StepStage.vue'
import CreditSpendCard from './CreditSpendCard.vue'
import CountryFlag from 'vue-country-flag'
import aiworkflowApi from '@/services/aiworkflowApi'
import creditsApi from '@/services/creditsApi'
import { BACKUP_UPLOADER_URL, ENV_CONFIG } from '@/config/envs'
import Portuguese from '@uppy/locales/lib/pt_BR'
import aiWorkflowMixin from '@/utils/aiWorkflow.mixin'
import _ from 'lodash'
import formatTimeMixin from '@/utils/formatTime.mixins.js'
import emptyState from '@/components/emptyState.vue'
import batchAiGenerationDubbingModal from '@/utils/batchAiGenerationDubbingModal.mixin.js'

export default {
  name: 'AddDubbingModal',
  mixins: [aiWorkflowMixin, formatTimeMixin, batchAiGenerationDubbingModal],
  components: {
    BaseBtn,
    CountryFlag,
    BaseModal,
    DefaultInitialState,
    StepStage,
    UploadStage,
    CreditSpendCard,
    emptyState,
  },
  data: () => ({
    creditAmount: 0,
    uploadFiles: [],
    uppy: {},
    uppyUpdated: 0,
    uppyCompletedOnes: {
      successfuly: [],
      failed: [],
    },
    generateDubbingDataInitial: {},
    generateDubbingData: {
      dubbingOption: 0,
      dubbingFromVideo: '',
      dubbingFromSubtitle: '',
      dubbingSelectedVoices: ['original'],
      searchAvailableLangsFilter: '',
      selectedItems: [],
      sameVoiceToAll: true,
      context: '',
    },
    voicePaths: [
      'Adam-male-pNInz6obpgDQGcFmaJgB',
      'Antoni-male-ErXwobaYiN019PkySvjV',
      'Arnold-male-VR6AewLTigWG4xSOukaG',
      'Bella-female-EXAVITQu4vr4xnSDxMaL',
      'Charlotte-female-XB0fDUnXU5powFXDhCwa',
      'Clyde-male-2EiwWnXFnvU5JabPnv8n',
      'Dave-male-CYw3kZ02Hs0563khs1Fj',
      'Domi-female-AZnzlk1XvdvUeBnXmlld',
      'Emily-female-LcfcDJNUP1GQjkzn1xUU',
      'Fin-female-D38z5RcWu1voky8WS1ja',
      'Matthew-male-Yko7PKHZNXotIFUBG7I9',
      'Patrick-male-ODq5zmih8GrVes37Dizd',
      'Rachel-female-21m00Tcm4TlvDq8ikWAM',
    ],
    audioPlayState: {
      isPlaying: [],
      isFetchingAudio: [],
      audioBlobUrls: {},
      audioURL: {
        originalURL: '',
        blobURL: '',
      },
    },
    currentModalStage: {
      globalStage: 'initial', // initial, upload, generate
      subStage: 1,
    },
    errors: {},
    loadings: {
      uploadDubbing: false,
      generateDubbing: false,
    },
    videoId: '',
    dubbingAiPrice: 1, // promotional - was 5
  }),
  computed: {
    ...mapState({
      video: (state) => state.video.video,
      subtitles: (state) => state.video?.video?.config?.subtitles || [],
      dubbingUploadData: (state) => state.video.dubbingData.upload,
    }),
    ...mapState('user', {
      planInfo: 'userPlanInfo',
    }),
    formatVideoTime() {
      return this.formatTime(this.video.length)
    },
    creditDubRemainCalc() {
      return this.creditAmount - this.generatedDubbingTotalPrice
    },
    currentChosenOption() {
      if (this.generateDubbingData.dubbingOption === 0)
        return this.generateDubbingData.dubbingFromVideo
      return this.generateDubbingData.dubbingFromSubtitle
    },
    isSourceItemSelected() {
      return this.generateDubbingData.selectedItems.some((item) =>
        this.checkIfListItemIsSource(item)
      )
    },
    generatedDubbingTotalPrice() {
      if (this.isBatch) {
        return this.batchDubTotalPrice
      }
      const IsDubbingFromSub =
        this.generateDubbingData.dubbingFromSubtitle.length > 0
      const isAutoDubbing =
        this.generateDubbingData.dubbingFromVideo.toLowerCase() === 'auto'
      const selectedItemsCount = this.generateDubbingData.selectedItems.length

      if (!selectedItemsCount) return 0

      const videoTimeMinutes = Math.floor(this.video.length / 60)
      if (!videoTimeMinutes) {
        if (isAutoDubbing || IsDubbingFromSub) {
          return this.dubbingAiPrice * selectedItemsCount
        } else {
          return this.dubbingAiPrice * (selectedItemsCount - 1)
        }
      }

      const remainingSeconds = this.video.length % 60
      let totalPrice =
        videoTimeMinutes * this.dubbingAiPrice +
        (remainingSeconds > 0 ? this.dubbingAiPrice : 0)

      if (isAutoDubbing || IsDubbingFromSub) {
        return totalPrice * selectedItemsCount
      }

      return selectedItemsCount >= 3 ? totalPrice * 2 : totalPrice
    },
    availableAiVoices() {
      const aiVoices = []
      this.voicePaths.forEach((item) => {
        const splittedItem = item.split('-')
        const itemLabel = `${splittedItem[0]}`
        aiVoices.push({
          label: itemLabel,
          value: item,
        })
      })
      return [
        {
          label: this.$i18next.t(
            'message.ai.dubbings.modal.auto.step_three.cloneVoice'
          ),
          value: 'original',
        },
        ...aiVoices,
      ]
    },
    hasDuplicatedLangs() {
      const dubbingLabels = this.dubbingUploadData.dubbingListToBeUploaded.map(
        (item) => item.label
      )
      return this.containsDuplicates(dubbingLabels)
    },
    isUploadDubbingBtnDisabled() {
      if (this.currentModalStage.subStage === 1) return true
      if (this.currentModalStage.subStage === 2 && this.hasDuplicatedLangs)
        return true
      if (this.isAnyLoadingRunning) return true
      return false
    },
    availableSubtitleLangsToDub() {
      const audiosList = this.getUnusedDubbingItemsForSubtitlesGen(
        this.availableAiLangCodes
      ).map((item) => item.srclang)
      return this.subtitles.filter(({ srclang }) =>
        audiosList.includes(srclang)
      )
    },
    showCardButtons() {
      if (this.currentModalStage.globalStage === 'generate') {
        if (this.currentModalStage.subStage < 4) return true
      } else if (this.currentModalStage.globalStage === 'upload') {
        if (this.currentModalStage.subStage < 4) return true
      } else if (this.currentModalStage.globalStage !== 'initial') return true
      return false
    },
    currentDubVoiceListing() {
      if (this.generateDubbingData.dubbingOption === 0) {
        return this.selectedItemsToBeGenerated
      }
      return this.generateDubbingData.selectedItems
    },
    initialStageCardsData() {
      return [
        {
          title: this.$i18next.t('message.ai.auto'),
          description: this.$i18next.t(
            'message.ai.dubbings.modal.auto.description'
          ),
          actionBtnText: this.$i18next.t(
            'message.ai.dubbings.modal.auto.modalAction'
          ),
          action: this.goToGenerateStage,
          isAi: true,
          isBeta: true,
          imgSource: require('@/assets/robot.svg'),
        },
        {
          title: this.$i18next.t('message.ai.upload'),
          description: this.$i18next.t(
            'message.ai.dubbings.modal.upload.description'
          ),
          actionBtnText: this.$i18next.t(
            'message.ai.dubbings.modal.upload.modalAction'
          ),
          action: this.goToUploadStage,
          isAi: false,
          isBeta: true,
          imgSource: require('@/assets/upload.svg'),
        },
      ]
    },
    isAnyLoadingRunning() {
      return Boolean(
        Object.values(this.loadings).filter((value) => value).length
      )
    },
    selectedItemsToBeGenerated() {
      return this.generateDubbingData.selectedItems.filter(
        (item) => !this.checkIfListItemIsSource(item)
      )
    },
    isGenerateDubbingBtnDisabled() {
      let condition = true
      if (this.currentModalStage.subStage === 1) {
        if (
          this.generateDubbingData.dubbingOption === 0 &&
          !this.generateDubbingData.dubbingFromVideo
        )
          condition = false
        if (
          this.generateDubbingData.dubbingOption === 1 &&
          !this.generateDubbingData.dubbingFromSubtitle
        )
          condition = false
        if (!this.unusedDubbingLangCodesAi.length) condition = false
      }
      return this.loadings.generateDubbing || !condition
    },
    forwardBtnData() {
      const data = {
        isDisabled: false,
        isLoading: false,
        onClick: () => {},
        tooltip: '',
        label: '',
        condition: true,
        generationCompleted: false,
        isBatchGeneration: false,
      }

      const { globalStage, subStage } = this.currentModalStage

      data.onClick = this.handleNextStep

      if (globalStage === 'generate') {
        data.isLoading = this.loadings.generateDubbing
        data.isDisabled = this.isGenerateDubbingBtnDisabled
        data.label = this.$i18next.t(
          'message.ai.dubbings.modal.auto.step_one.actionBtn'
        )
        if (subStage === 1) {
          data.tooltip = !this.unusedDubbingLangCodesAi.length
            ? this.$i18next.t(
                'message.ai.dubbings.modal.auto.step_one.placeholder'
              )
            : false
          if (this.isBatch) {
            data.isBatchGeneration = true
            data.isLoading = this.loadingConfigs
            if (this.loadingConfigs) {
              data.tooltip = this.$i18next.t(
                'message.ai.subtitles.modal.auto.step_one.gettingReady'
              )
            }
          }
        }
        if (subStage === 2) {
          if (this.generateDubbingData.dubbingOption === 0) {
            const checkFinalOptionsSize = this.selectedItemsToBeGenerated.length
            const checkIfSourceItemIsSelected = this.isSourceItemSelected
            data.isDisabled =
              !checkFinalOptionsSize ||
              (this.sourceItem.srclang !== 'auto' &&
                !checkIfSourceItemIsSelected)
            if (!checkFinalOptionsSize)
              data.tooltip = this.$i18next.t(
                'message.ai.dubbings.modal.auto.step_two.tooltips.quantity'
              )
            if (
              this.sourceItem.srclang !== 'auto' &&
              !checkIfSourceItemIsSelected
            )
              data.tooltip = this.$i18next.t(
                'message.ai.dubbings.modal.auto.step_two.tooltips.source',
                { lang: this.sourceItem.label }
              )
            if (this.isBatch) {
              const isAuto =
                this.generateDubbingData.dubbingFromVideo === 'auto'
              const hasPossibleDubs = this.handleExistingDubs

              const selectedLangsCount =
                this.generateDubbingData.selectedItems.length

              if (hasPossibleDubs && selectedLangsCount > (isAuto ? 0 : 1)) {
                data.tooltip = this.$i18next.t(
                  'message.ai.dubbings.modal.auto.step_two.tooltips.dubsAlreadyExists'
                )
              }
              if (isAuto || checkIfSourceItemIsSelected) {
                data.isDisabled = hasPossibleDubs
              }
            }
          } else {
            const checkFinalOptionsSize =
              this.generateDubbingData.selectedItems.length
            data.isDisabled = !checkFinalOptionsSize
            if (!checkFinalOptionsSize)
              data.tooltip = this.$i18next.t(
                'message.ai.dubbings.modal.auto.step_two.tooltips.quantity'
              )
          }

          if (this.creditDubRemainCalc < 0) {
            data.isDisabled = this.creditDubRemainCalc < 0
            data.tooltip = this.$i18next.t('message.ai.notEnoughCredits')
          }
        }
        if (this.generateDubbingData.dubbingOption === 0) {
          if (subStage === 3) {
            data.label = this.$i18next.t(
              'message.ai.dubbings.modal.auto.step_four.actionBtn'
            )
          }
          if (subStage === 4) {
            data.condition = false
            if (this.isBatch) {
              data.generationCompleted = true
            }
          }
        } else {
          if (subStage === 3) {
            data.label = this.$i18next.t(
              'message.ai.dubbings.modal.auto.step_three.actionBtn'
            )
          }
        }
      }

      if (globalStage === 'upload') {
        data.isLoading = this.isAnyLoadingRunning
        data.isDisabled = this.isUploadDubbingBtnDisabled
        data.label = this.$i18next.t(
          'message.ai.dubbings.modal.upload.step_two.actionBtn'
        )
        if (subStage === 2) {
          data.tooltip = this.hasDuplicatedLangs
            ? this.$i18next.t(
                'message.ai.dubbings.modal.upload.step_two.tooltip'
              )
            : false
        }
        if (subStage === 3) {
          data.label = this.$i18next.t(
            'message.ai.dubbings.modal.upload.step_three.actionBtn'
          )
        }
      }

      return data
    },
    isActionButtonsHighlighted() {
      if (
        this.currentModalStage.globalStage === 'generate' &&
        this.currentModalStage.subStage === 2
      )
        return true
      return false
    },
    noDefaultPadding() {
      if (this.currentModalStage.globalStage !== 'initial') return true
      return false
    },
    checkboxIndeterminateStatus() {
      return (
        this.generateDubbingData.selectedItems.length > 0 &&
        this.generateDubbingData.selectedItems.length <
          this.filteredAiDubbingOptions.length
      )
    },
    isAnyAiDubbingItemSelected() {
      return this.generateDubbingData.selectedItems.length > 0
    },
    filteredAiDubbingOptions() {
      // get all available lang codes to generate, filtered by the search
      const dubbingList = !this.generateDubbingData.dubbingOption
        ? this.unusedDubbingForTranscription
        : this.unusedDubbingLangCodesAi
      let filteredDubbingOptions = dubbingList.filter(({ label }) => {
        return this.checkSearch(label)
      })
      if (this.isBatch) {
        filteredDubbingOptions = this.handleBatchDubbingOptions()
      }

      // sort results by placing disabled items at the top
      const sortedReturn = filteredDubbingOptions.sort((a, b) => {
        if (
          this.checkIfListItemIsSource(a) &&
          !this.checkIfListItemIsSource(b)
        ) {
          return -1
        } else if (
          !this.checkIfListItemIsSource(a) &&
          this.checkIfListItemIsSource(b)
        ) {
          return 1
        }
        return 0
      })

      return sortedReturn
    },
    unusedLangCodes() {
      return this.getUnusedDubbingItems(this.langCodes)
    },
    logoConfig() {
      return {
        top: -83,
        left: 295,
        size: 0.5,
        rotation: 0,
      }
    },
    arrowConfig() {
      return {
        top: -50,
        left: 100,
        size: 0.65,
        rotation: 100,
      }
    },
    textConfig() {
      return {
        text: this.$i18next.t('message.ai.aiWorkFlowChargeModal.emptyState'),
        top: 70,
        left: 235,
        size: '1em',
        rotation: 0,
        textAlign: 'center',
        textWidth: '40%',
        hrefText: this.$i18next.t(
          'message.ai.aiWorkFlowChargeModal.addCredits'
        ),
        onClick: this.handleGoChargeCredits,
      }
    },
    hasEnoughCredits() {
      const dubPrice = this.calcPrice()
      const hasEnoughCredits = this.calcEnoughCredits(dubPrice)
      return !(hasEnoughCredits < 0)
    },
    isStepTwo() {
      return this.currentModalStage.subStage === 2
    },
    isStepThree() {
      return this.currentModalStage.subStage === 3
    },
    isStepFour() {
      return this.currentModalStage.subStage === 4
    },
    nounNumber() {
      if (this.batchAllVideosCount > 1) return 'videos'
      return 'video'
    },
    isUploadState() {
      return this.currentModalStage.globalStage === 'upload'
    },
  },
  mounted() {
    this.$root.$on('addAudio', async (data) => {
      if (!Object.keys(this.uppy).length) return //eslint-disable-line

      let storageKeys = Storage.getStorageKeys()
      for (let key of storageKeys) {
        if (key[0].includes('tus::tus-uppy')) {
          Storage.removeItem(key[0])
        }
      }

      const addedFile = data.file
      const session = await store().dispatch('session/refreshSession')
      const token = session.accessToken.jwtToken
      const userId = this.$store.getters['session/userSub']
      const refreshToken = session.refreshToken.token

      if (token) {
        Storage.setItem('accessToken', session.accessToken.jwtToken)
      }

      const authorization = 'Bearer' + Storage.getItem('accessToken')

      try {
        this.uppy.addFile({
          source: 'file input',
          name: addedFile.name,
          type: addedFile.type,
          data: addedFile,
          meta: {
            video_id: data.video_id,
            filetype: 'audio',
            lang: data.srclang,
            user_id: userId,
            refresh_token: refreshToken,
            authorization: authorization,
          },
        })
      } catch (e) {
        console.error('ERROR UPLOADING AUDIO: ', e)
        if (e.isRestriction && e.message.match(/duplicate/i)) {
          const fileName = addedFile.name
          const lastCharacters = fileName.substring(fileName.length - 3)
          const regex = /\([0-9]+\)/
          const regexTest = regex.test(lastCharacters)
          let fileNumber = fileName.slice(-2).charAt(0)
          if (regexTest) {
            const newFileNumber = Number(fileNumber) + 1
            Object.defineProperty(addedFile, 'name', {
              writable: true,
              value: addedFile.name.slice(0, -3) + ` (${newFileNumber})`,
            })
          } else {
            Object.defineProperty(addedFile, 'name', {
              writable: true,
              value: addedFile.name + ' (1)',
            })
          }

          this.$root.$emit('addAudio', data)
        } else {
          this.loadings.uploadDubbing = false
        }
      }
    })
  },
  watch: {
    open(val) {
      if (!val) this.handleClose()
    },
    subtitles() {
      this.checkDubbingOption()
    },
  },
  methods: {
    ...mapMutations('video', [
      'UPDATE_AUDIO_LIST',
      'UPDATE_GENERATING_DUBBING_DATA',
    ]),
    getItemCost(item) {
      let videoTime = Math.floor(this.video.length / 60)
      if (!videoTime) videoTime = 1
      const calc = this.getMultiplierAmount(item) * videoTime
      return calc ? calc : 1
    },
    checkDubbingOption() {
      if (this.subtitles.length) {
        this.generateDubbingData.dubbingOption = 1
      } else {
        this.generateDubbingData.dubbingOption = 0
      }
    },
    checkIfDubHasTranslation(srclang) {
      return this.subtitles.some((item) => item.srclang === srclang)
    },
    getMultiplierAmount(item) {
      const additional = this.checkIfDubHasTranslation(item.srclang) ? 0 : 1
      if (this.sourceItem.srclang !== 'auto') {
        if (this.checkIfListItemIsSource(item) && !this.subtitles.length) {
          return 5 + 3 + additional
        }
        return 5 + additional
      }
      if (
        this.generateDubbingData.selectedItems.length &&
        this.generateDubbingData.selectedItems[0].srclang === item.srclang &&
        !this.subtitles.length
      ) {
        return 5 + 3 + additional
      }
      return 5 + additional
    },
    handleSetSameVoiceToAll() {
      if (this.generateDubbingData.sameVoiceToAll) {
        const firstVoice = this.generateDubbingData.dubbingSelectedVoices[0]
        this.generateDubbingData.dubbingSelectedVoices =
          this.generateDubbingData.dubbingSelectedVoices.map(() => firstVoice)
      }
    },
    canShowRowValues(item) {
      let isSourceItemSelected = this.isSourceItemSelected
      if (this.sourceItem.srclang === 'auto') {
        isSourceItemSelected = Boolean(
          this.generateDubbingData.selectedItems.length
        )
      }

      return (
        (this.checkIfListItemIsSource(item) && isSourceItemSelected) ||
        (!this.checkIfListItemIsSource(item) &&
          isSourceItemSelected &&
          this.checkIfItemIsSelected(item))
      )
    },
    checkIfItemIsSelected({ srclang }) {
      return this.generateDubbingData.selectedItems.some(
        (item) => item.srclang === srclang
      )
    },
    async handlePlayCurrentAudio(item, index) {
      if (!this.$refs.audioPlay.paused) {
        this.$refs.audioPlay.pause()
        this.audioPlayState.isPlaying = []
      }
      this.audioPlayState.isPlaying.push(item.country)
      let audioLang = item.country ?? 'us'
      const newAudioURL = `https://s3.us-east-1.wasabisys.com/panda-speech-cache/voices/${this.generateDubbingData.dubbingSelectedVoices[index]}-${audioLang}.mp3`
      if (this.audioPlayState.audioBlobUrls[newAudioURL]) {
        this.audioPlayState.audioURL.originalURL = newAudioURL
        this.audioPlayState.audioURL.blobURL =
          this.audioPlayState.audioBlobUrls[newAudioURL]
      } else if (this.audioPlayState.audioURL.originalURL !== newAudioURL) {
        this.audioPlayState.isFetchingAudio.push(item.country)
        const mp3Response = await fetch(newAudioURL)
        const blobMp3Response = await mp3Response.blob()
        const objectURL = URL.createObjectURL(blobMp3Response)

        this.audioPlayState.audioURL.originalURL = newAudioURL
        if (this.audioPlayState.audioURL.blobURL !== objectURL) {
          this.audioPlayState.audioBlobUrls[newAudioURL] = objectURL
          this.audioPlayState.audioURL.blobURL = objectURL
        }
      }
      this.$refs.audioPlay.currentTime = 0
      this.$refs.audioPlay.volume = 1
      this.$refs.audioPlay.addEventListener('ended', () => {
        const itemCountryIndex = this.audioPlayState.isPlaying.indexOf(
          item.country
        )
        this.audioPlayState.isPlaying.splice(itemCountryIndex, 1)
      })
      this.$refs.audioPlay.oncanplay = () => {
        const isLoadingIndex = this.audioPlayState.isFetchingAudio.indexOf(
          item.country
        )
        if (isLoadingIndex >= 0)
          this.audioPlayState.isFetchingAudio.splice(isLoadingIndex, 1)
        this.$refs.audioPlay.play()
      }
    },
    handleStopCurrentAudio(item) {
      if (item === undefined) {
        this.audioPlayState.isPlaying = []
      } else {
        const itemCountryIndex = this.audioPlayState.isPlaying.indexOf(
          item.country
        )
        this.audioPlayState.isPlaying.splice(itemCountryIndex, 1)
      }
      this.$refs.audioPlay.pause()
    },
    setUppy() {
      const objTus = this.handleTusObject()
      if (!this.isChrome() || this.isMobile()) {
        objTus.resume = false
        objTus.parallelUploads = 1
      }

      const instanceUppy = new Uppy({
        autoProceed: true,
        allowMultipleUploads: true,
        restrictions: {
          allowedFileTypes: ['audio/*'],
        },
        locale: Portuguese,
      }).use(tus, objTus)
      this.uppy = instanceUppy

      this.uppy.on('progress', () => {
        this.uploadFiles = this.uppy.getFiles()

        if (this.uploadFiles.length) {
          this.dubbingUploadData.dubbingListToBeUploaded =
            this.dubbingUploadData.dubbingListToBeUploaded.map((item) => {
              const hasUploadItem = this.uploadFiles.filter((uploadItem) => {
                if (uploadItem.meta.lang === item.srclang) return uploadItem
              })
              if (hasUploadItem.length) {
                return { ...item, progress: hasUploadItem[0].progress }
              }
              return item
            })
        }
      })
      this.uppy.on('upload-error', (file, error) => {
        console.error(file, error)

        this.dubbingUploadData.dubbingListToBeUploaded =
          this.dubbingUploadData.dubbingListToBeUploaded.map((item) => {
            if (item.srclang === file.meta.lang) {
              return { ...item, error: true }
            }
            return item
          })
      })
      this.uppy.on('file-added', (file) => {
        let storageKeys = Storage.getStorageKeys()
        for (let key of storageKeys) {
          if (key[0].includes('tus::tus-uppy')) {
            Storage.removeItem(key[0])
          }
        }
        const userId = this.$store.getters['session/userSub']
        const refreshToken = this.$store.state.user.refreshToken
        const authorization = 'Bearer ' + Storage.getItem('accessToken')
        this.uppy.setFileMeta(file.id, {
          video_id: this.videoId,
          filetype: 'audio',
          lang: file.meta.lang,
          user_id: userId,
          refresh_token: refreshToken,
          authorization: authorization,
        })
        this.uploadFiles = this.uppy.getFiles()
      })
      this.uppy.on('upload-success', (file) => {
        this.uploadFiles = this.uppy.getFiles()

        this.dubbingUploadData.dubbingListToBeUploaded =
          this.dubbingUploadData.dubbingListToBeUploaded.map((item) => {
            if (item.srclang === file.meta.lang) {
              return { ...item, completed: true }
            }
            return item
          })
      })
      this.uppy.on('complete', () => {
        const finalItems =
          this.dubbingUploadData.dubbingListToBeUploaded.filter(
            (item) => item.completed || item.error
          )

        if (finalItems.length === this.uploadFiles.length) {
          const audioFormattedDubbingList =
            this.dubbingUploadData.dubbingListToBeUploaded
              .filter((item) => item.completed)
              .map((item) => {
                return {
                  lang: item.srclang,
                  type: 'UPLOADED',
                }
              })
          this.UPDATE_AUDIO_LIST([
            ...this.activeAudios,
            ...audioFormattedDubbingList,
          ])
          window.db.videos.update(this.videoId, {
            audios: this.activeAudios.map((item) => item.lang),
          })
          this.loadings.uploadDubbing = false
        }
      })
    },
    setUppyEventsOff() {
      this.uppy.off('file-added')
      this.uppy.off('upload-success')
      this.uppy.off('progress')
      this.uppy.off('restriction-failed')
      this.uppy.off('back-online')
      this.uppy.off('upload-error')
    },
    handleTusObject() {
      let uploaderUrl = `${BACKUP_UPLOADER_URL}/files/`
      if (ENV_CONFIG !== 'production') {
        uploaderUrl = `${BACKUP_UPLOADER_URL}:8443/files/`
      }
      const storageUrl = Storage.getItem('uploaderUrlv3')
      if (storageUrl) {
        uploaderUrl = `${storageUrl}/files/`
        if (ENV_CONFIG !== 'production') {
          uploaderUrl = `${storageUrl}:8443/files/`
        }
      }
      let objTus = {
        id: 'tus',
        endpoint: uploaderUrl,
        resume: true,
        retryDelays: [0, 1000],
        parallelUploads: 5,
        limit: 2,
      }
      if (!this.isChrome() || this.isMobile()) {
        objTus.resume = false
        objTus.parallelUploads = 1
      }
      return objTus
    },
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    },
    isChrome() {
      return !!window.chrome
    },
    async getCreditsInfo() {
      try {
        const { data } = await creditsApi.getCreditsBalance()
        this.creditAmount = data.balance
      } catch (error) {
        console.error('error', error)
      }
    },
    onOpenHook({ videoId, initialState, isBatch }) {
      this.getCreditsInfo()
      if (isBatch) {
        this.isBatch = isBatch
        this.handleOpenBatchDubGenerationModal()
        return
      } else {
        this.isBatch = false
      }
      this.generateDubbingDataInitial = _.cloneDeep(this.generateDubbingData)
      this.videoId = videoId
      this.open = true
      this.setUppy()

      this.checkDubbingOption()

      if (this.video.original_lang) {
        this.generateDubbingData.dubbingFromVideo = this.video.original_lang
      } else if (this.planInfo.currency) {
        if (this.planInfo.currency === 'brl') {
          this.generateDubbingData.dubbingFromVideo = 'pt-BR'
        } else {
          this.generateDubbingData.dubbingFromVideo = 'en'
        }
      } else {
        this.generateDubbingData.dubbingFromVideo = 'auto'
      }

      if (this.subtitles.length) {
        this.generateDubbingData.dubbingFromSubtitle = this.subtitles[0].srclang
      }

      if (initialState) {
        this.handleUpdateModalStage({
          globalStage: 'generate',
          subStage: 1,
        })
      }
    },
    updateSubStage(subStage) {
      this.currentModalStage.subStage = subStage
    },
    selectAllItems() {
      let arr = []

      if (
        this.generateDubbingData.selectedItems.length !==
        this.filteredAiDubbingOptions.length
      ) {
        arr = this.filteredAiDubbingOptions
      }

      this.generateDubbingData.selectedItems = arr
    },
    checkboxSelection(item) {
      let alreadySelected = this.checkIfSelected(item)
      if (alreadySelected) {
        let newArr = [
          ...this.generateDubbingData.selectedItems.filter((lookItem) => {
            return lookItem.srclang !== item.srclang
          }),
        ]
        this.generateDubbingData.selectedItems = [...newArr]
      } else {
        this.generateDubbingData.selectedItems.push(item)
      }
    },
    checkIfSelected(item) {
      let foundItem = this.generateDubbingData.selectedItems.find(
        (searchedItem) => {
          return searchedItem.srclang === item.srclang
        }
      )
      if (foundItem) return true
      return false
    },
    checkSearch(label) {
      if (this.generateDubbingData.searchAvailableLangsFilter) {
        return this.areStringsEqualIgnoringCaseAndSymbols(
          this.generateDubbingData.searchAvailableLangsFilter,
          label
        )
      }
      return true
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
    },
    handleGoBack() {
      this.clearErrors()
      let jumpAmount = 1
      if (this.currentModalStage.subStage > 1) {
        if (
          this.currentModalStage.globalStage === 'generate' &&
          this.currentModalStage.subStage === 2
        ) {
          this.generateDubbingData.selectedItems = []
        }
        return (this.currentModalStage.subStage -= jumpAmount)
      }
      this.goToInitial()
    },
    async handleNextStep() {
      let { globalStage, subStage } = this.currentModalStage
      let jumpStageAmount = 1

      if (globalStage === 'upload') {
        this.currentModalStage.subStage += jumpStageAmount
        if (subStage === 2) {
          this.loadings.uploadDubbing = true

          this.dubbingUploadData.dubbingListToBeUploaded.map((item) => {
            this.$root.$emit('addAudio', {
              ...item,
              ...{ video_id: this.videoId },
            })
          })
        }
        if (subStage === 3) {
          this.handleClose()
        }
      }

      if (globalStage === 'generate') {
        let stepAdditionalFunction = async () => {}
        if (this.generateDubbingData.dubbingOption === 0) {
          const stepActions = {
            1: () => {
              if (this.currentChosenOption !== 'auto') {
                this.sourceItem = this.getLangCodeBySrcLang(
                  this.currentChosenOption
                )
                this.generateDubbingData.selectedItems.push(this.sourceItem)
              } else {
                this.sourceItem = this.availableTranscriptionLangCodes[0]
              }
            },
            2: () => {
              this.generateDubbingData.dubbingSelectedVoices = [
                ...this.availableAiVoices
                  .slice(0, this.selectedItemsToBeGenerated.length)
                  .map((item) => item.value),
              ]
              this.handleSetSameVoiceToAll()
            },
            3: async () => {
              if (this.isBatch) {
                await this.handleBatchDubGeneration()
              } else {
                await this.handlePostDubbing()
              }
            },
            4: this.handleClose,
          }
          stepAdditionalFunction =
            stepActions[subStage] ?? stepAdditionalFunction
        } else {
          const stepActions = {
            1: () => {
              this.sourceItem = this.getLangCodeBySrcLang(
                this.currentChosenOption
              )
              if (this.isNotBlockedAsOriginalLang(this.sourceItem)) {
                this.generateDubbingData.selectedItems.push(this.sourceItem)
              }
            },
            2: () => {
              this.generateDubbingData.dubbingSelectedVoices = [
                ...this.availableAiVoices
                  .slice(0, this.generateDubbingData.selectedItems.length)
                  .map((item) => item.value),
              ]
              this.handleSetSameVoiceToAll()
            },
            3: async () => {
              if (this.isBatch) {
                jumpStageAmount = 2
                await this.handleBatchDubGeneration()
              } else {
                jumpStageAmount = 2
                await this.handlePostDubbing()
              }
            },
            4: this.handleClose,
          }
          stepAdditionalFunction =
            stepActions[subStage] ?? stepAdditionalFunction
        }
        try {
          await stepAdditionalFunction()
          if (this.isBatch && this.isStepThree) {
            this.loadings.generateDubbing = true
            setTimeout(() => {
              this.currentModalStage.subStage += jumpStageAmount
              this.loadings.generateDubbing = false
            }, 2000)
          } else {
            this.currentModalStage.subStage += jumpStageAmount
          }
        } catch (err) {
          console.error(err)
        }
      }
    },
    getVoiceId(voice) {
      const voiceName = this.getVoiceName(voice)
      const finalVoiceId = `${voiceName}${
        voiceName !== 'original'
          ? '-' + this.currentDubVoiceListing[0].country
          : ''
      }`
      return finalVoiceId
    },
    getVoiceName(voice) {
      const splittedVoiceId = voice.split('-')
      const voiceName = splittedVoiceId[splittedVoiceId.length - 1]
      return voiceName
    },
    async handlePostDubbing() {
      this.loadings.generateDubbing = true
      let data = []
      let fromLang = this.generateDubbingData.dubbingFromVideo
      try {
        if (this.generateDubbingData.dubbingOption === 0) {
          const voiceId = this.getVoiceId(
            this.generateDubbingData.dubbingSelectedVoices[0]
          )
          const additionalConfig = {
            background_sound_mode: 'ai',
            voice_id: voiceId,
          }
          let selectedItemsSrcLang = this.currentDubVoiceListing
          if (selectedItemsSrcLang.length) {
            selectedItemsSrcLang = selectedItemsSrcLang.map((item, index) => {
              let returnObj = { lang: item.srclang }
              const voiceName = this.getVoiceName(
                this.generateDubbingData.dubbingSelectedVoices[index]
              )
              returnObj.voice_id = voiceName
              return returnObj
            })
            additionalConfig.generate_dubbing = true
          }
          data = await aiworkflowApi.createTranscription({
            fromLang,
            videoId: this.videoId,
            ctx: this.generateDubbingData.context,
            additionalLangs: selectedItemsSrcLang,
            additionalConfig,
          })
          if (
            this.generateDubbingData.dubbingFromVideo &&
            this.generateDubbingData.dubbingFromVideo !== 'auto'
          ) {
            this.UPDATE_VIDEO_ORIGINAL_LANG(
              this.generateDubbingData.dubbingFromVideo
            )
            this.$root.$emit('updateVideoInfo', this.video)
          }
        } else {
          const additionalConfig = {
            background_sound_mode: 'ai',
          }
          let selectedItemsSrcLang = this.currentDubVoiceListing
          if (selectedItemsSrcLang.length) {
            selectedItemsSrcLang = selectedItemsSrcLang.map((item, index) => {
              let returnObj = { lang: item.srclang }
              const voiceName = this.getVoiceName(
                this.generateDubbingData.dubbingSelectedVoices[index]
              )
              returnObj.voice_id = voiceName
              return returnObj
            })
            additionalConfig.generate_dubbing_for_additional_langs = true
          }
          let subtitlesLangList = []
          if (this.isBatch) {
            subtitlesLangList = this.batchSubtitles
          } else {
            subtitlesLangList = this.subtitles.map((item) => item.srclang)
          }
          const promises = selectedItemsSrcLang.map((item) => {
            if (subtitlesLangList.includes(item.lang)) {
              return aiworkflowApi.createDubbing({
                fromLang: this.getPreparedSrcLang(item.lang),
                videoId: this.videoId,
                voiceId: item.voice_id,
              })
            }
            const additionalConfig = {
              generate_dubbing: true,
              voice_id: item.voice_id,
              background_sound_mode: 'ai',
            }
            return aiworkflowApi.createTranslation({
              fromLang: this.getPreparedSrcLang(
                this.generateDubbingData.dubbingFromSubtitle
              ),
              videoId: this.videoId,
              toLang: item.lang,
              additionalConfig,
            })
          })
          data = await Promise.all(promises)
        }
        this.$root.$emit('addAiWorkflowDubbing', data.data)
        this.loadings.generateDubbing = false
      } catch (err) {
        if (this.isBatch) {
          this.currentModalStage.subStage = 4
        } else {
          console.error(err)
          if (
            err?.response?.data &&
            err?.response?.data?.errMsg &&
            err?.response?.data?.errCode
          ) {
            const badRequestAllowedValues =
              'should be equal to one of the allowed values: en, en-US, pt, auto, pt-BR, es, es-MX, en-UK, it, it-ch, fr, fr-be, fr-ca, fr-lu, fr-ch, de, de-at, de-li, de-lu, de-ch.'
            this.errors.generate = `${err.response.data.errCode}: ${err.response.data.errMsg}`
            if (err.response.data.errMsg.includes(badRequestAllowedValues)) {
              this.errors.generate = `${i18n.t(
                'message.ai.noWordsFound'
              )}: ${i18n.t('message.ai.skippedWarningNotSupported')}`
            }
          } else if (err?.message) {
            this.errors.generate = err.message
          }
          this.loadings.generateDubbing = false
          throw err
        }
      }

      this.$root.$emit('reloadBalance')
      this.$root.$emit('reloadBalanceDrawer')
    },
    handleClose() {
      this.$root.$emit('closeDubbingModal')
    },
    onCloseHook() {
      this.generateDubbingData = _.cloneDeep(this.generateDubbingDataInitial)
      this.handleResetUppy()
      this.clearErrors()
      this.goToInitial()
    },
    handleResetUppy() {
      this.uploadFiles = []
      this.uppy = {}
    },
    goToGenerateStage() {
      this.handleUpdateModalStage({
        globalStage: 'generate',
        subStage: 1,
      })
    },
    goToUploadStage() {
      this.handleUpdateModalStage({
        globalStage: 'upload',
        subStage: 1,
      })
    },
    goToInitial() {
      this.handleUpdateModalStage()
    },
    handleUpdateModalStage({ globalStage = 'initial', subStage = 1 } = {}) {
      this.currentModalStage.globalStage = globalStage
      this.currentModalStage.subStage = subStage
    },
    handleGoChargeCredits() {
      this.handleClose()
      return this.$router.push({ name: 'Credits', query: { charge: 'true' } })
    },
    calcEnoughCredits(price) {
      return this.creditAmount - price
    },
    calcPrice() {
      if (this.isBatch) {
        return this.batchDubTotalPrice
      }
      const IsDubbingFromSub =
        this.generateDubbingData.dubbingFromSubtitle.length > 0

      const isAutoDubbing =
        this.generateDubbingData.dubbingFromVideo.toLowerCase() === 'auto'

      const selectedItemsCount = this.generateDubbingData.selectedItems.length

      const videoTimeMinutes = Math.floor(this.video.length / 60)
      if (!videoTimeMinutes) return this.dubbingAiPrice

      const remainingSeconds = this.video.length % 60
      let totalPrice =
        videoTimeMinutes * this.dubbingAiPrice +
        (remainingSeconds > 0 ? this.dubbingAiPrice : 0)

      if (isAutoDubbing || IsDubbingFromSub) {
        return totalPrice * selectedItemsCount
      }

      return selectedItemsCount >= 3 ? totalPrice * 2 : totalPrice
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_ai_works';

.list-item__text {
  white-space: nowrap;
  margin-left: 10px;
  width: 140px;
}
.container__search {
  width: 181px;
}
.select__dubbing-option {
  width: 315px;

  .v-input__custom-colorized {
    width: 216px;
  }
}

.container__content-list {
  width: 552px;
  margin: auto;
}

.v-simple-checkbox {
  i {
    color: var(--v-primary-base) !important;
  }
}

::v-deep .container__upload-list--modal {
  &.padded {
    margin-top: 22px;
  }
}

.container {
  &__setup-action {
    width: 347px;
    margin: auto;
  }

  &__final-screen {
    width: 410px;
    margin: auto;
  }

  &__ai-context {
    width: 589px;
    margin: auto;
    height: 248px;
  }
}

.no-ripple {
  &::after {
    display: none !important;
  }
}

.container__credit-spend {
  width: 400px;
  position: relative;
  left: 0;
  margin: auto;
}
.bg-color {
  background-color: var(--v-newBackground-base) !important;
}
.card-height {
  height: auto !important;
}
.card-height-stp4 {
  max-height: 90px !important;
  margin-bottom: 0px !important;
  padding-top: 25px !important;
}
.hide-card {
  visibility: hidden;
}
.btn-width {
  width: 100px;
}
</style>

<style lang="scss">
.bg-color-icon {
  .v-image__image {
    background-color: var(--v-newBackground-base) !important;
  }
}

@media screen and (max-height: 768px) {
  .v-card__actions--bot-position {
    height: 80%;
  }

  .credit-spend-card {
    width: 64%;
  }
  .credit-spend-col {
    display: flex;
    align-items: center;
  }
}
</style>
